// Spec #14: consume the email verification link (?token=...).
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { AuthLayout } from '@/app/AuthLayout';

export function VerifyEmailScreen() {
  const { verifyEmail } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [busy, setBusy] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setBusy(false);
        setError('Missing verification token.');
        return;
      }
      try {
        const ok = await verifyEmail(token);
        if (cancelled) return;
        setVerified(ok);
        if (!ok) setError('This verification link is invalid or has expired.');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Verification failed.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, verifyEmail]);

  return (
    <AuthLayout
      title={busy ? 'Verifying your email' : verified ? 'Email verified' : 'Verification failed'}
    >
      {busy ? (
        <div className="muted">Hold on while we confirm your verification link…</div>
      ) : verified ? (
        <div className="stack">
          <div className="info-banner">Thanks — your email is now verified.</div>
          <button className="btn btn--primary" onClick={() => navigate('/home', { replace: true })}>
            Continue to FieldReport
          </button>
        </div>
      ) : (
        <div className="stack">
          <div className="error-banner">{error ?? 'Verification failed.'}</div>
          <Link to="/login" className="btn">
            Back to sign in
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}
